import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  getActiveTabPath,
  getPreserveSearchPaths,
  getPreserveSearchPathsKey,
  persistTabSearch,
} from '../helpers/fingo-paper-tabs';

const useTabSearch = ({ tabs, persist = true }) => {
  const { path: routePath } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const defaultTabPath = tabs[0]?.path;
  const preserveSearchPathsKey = getPreserveSearchPathsKey(tabs);
  const preserveSearchPaths = useMemo(
    () => getPreserveSearchPaths(preserveSearchPathsKey),
    [preserveSearchPathsKey],
  );
  const enabled = preserveSearchPaths.length > 0;
  const preserveSearchPathsRef = useRef(preserveSearchPaths);
  preserveSearchPathsRef.current = preserveSearchPaths;

  const activeTabPath = enabled && defaultTabPath
    ? getActiveTabPath(location.pathname, routePath, defaultTabPath)
    : null;
  const search = location.search;

  const shouldPersistTab = (tabPath) => (
    enabled && preserveSearchPathsRef.current.includes(tabPath)
  );

  useEffect(() => {
    if (!persist || !enabled || !defaultTabPath) return undefined;

    const unsubscribe = history.listen((nextLocation) => {
      const tabPath = getActiveTabPath(
        nextLocation.pathname,
        routePath,
        defaultTabPath,
      );
      if (!shouldPersistTab(tabPath)) return;
      persistTabSearch(routePath, tabPath, nextLocation.search);
    });

    return unsubscribe;
  }, [persist, enabled, defaultTabPath, routePath, history]);

  useEffect(() => {
    if (!persist || !activeTabPath || !shouldPersistTab(activeTabPath)) return;
    persistTabSearch(routePath, activeTabPath, search);
  }, [persist, activeTabPath, search, routePath]);

  const tabSearch = enabled
    ? {
      preserveSearchPaths,
      defaultTabPath,
      activeTabPath,
      currentSearch: search,
    }
    : {};

  return {
    defaultTabPath,
    tabSearch,
  };
};

export default useTabSearch;
